/* ============================================================
   Rukkumani Motion Engine
   - IntersectionObserver-based reveal-on-scroll (fade + lift + stagger)
   - Subtle parallax for elements with [data-rk-parallax]
   - Video hero: autoplay muted, pause when off-screen
   - Hover-play product card videos
   - Respects prefers-reduced-motion
   ============================================================ */

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll (fade + lift) ---------- */
  function initReveal() {
    if (reduceMotion) {
      document.querySelectorAll('.rk-reveal, .rk-stagger > *').forEach((el) => {
        el.classList.add('is-visible');
      });
      return;
    }

    const targets = document.querySelectorAll(
      '.rk-reveal, .rk-stagger, [data-rk-reveal]'
    );
    if (!targets.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;

          if (el.classList.contains('rk-stagger')) {
            const children = Array.from(el.children);
            children.forEach((child, i) => {
              child.style.transitionDelay = i * 80 + 'ms';
              child.classList.add('is-visible');
            });
          } else {
            el.classList.add('is-visible');
          }

          io.unobserve(el);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    targets.forEach((el) => io.observe(el));
  }

  /* ---------- Auto-apply parallax to hero / lookbook / feature media ---------- */
  function autoTagParallax() {
    if (reduceMotion) return;
    const selectors = [
      '.banner--full-width .banner__media img',
      '.banner__media img',
      '.image-banner__media img',
      '.image-with-text__media img',
      '.collage .media img',
      '.rk-lookbook__image img',
      '.rk-video-feature__media > video',
      '.rk-video-feature__media > img',
    ];
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (el.closest('[data-rk-no-parallax]')) return;
        if (el.hasAttribute('data-rk-parallax')) return;
        // Stronger parallax for feature media, subtle for everything else
        const speed = sel.startsWith('.rk-video-feature') ? '0.22' : '0.18';
        el.setAttribute('data-rk-parallax', speed);
        // Make sure the element can move beyond its natural box
        if (sel.startsWith('.rk-video-feature')) {
          el.style.willChange = 'transform';
          el.style.transform = 'translate3d(0,0,0)';
        }
        const parent = el.parentElement;
        if (parent && getComputedStyle(parent).overflow === 'visible') {
          parent.style.overflow = 'hidden';
        }
      });
    });
  }

  /* ---------- Word-by-word heading reveal ---------- */
  function splitHeadingsForReveal() {
    if (reduceMotion) return;
    const headings = document.querySelectorAll(
      '.rk-video-feature__heading, .rk-video-hero__heading, .rk-section-eyebrow + h2'
    );
    headings.forEach((h) => {
      if (h.dataset.rkSplit) return;
      h.dataset.rkSplit = '1';
      const original = h.innerHTML;
      // Preserve nested <span> like .rk-script
      const children = Array.from(h.childNodes);
      const newHtml = children.map((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent
            .split(/(\s+)/)
            .map((part) => {
              if (/^\s+$/.test(part)) return part;
              if (!part) return '';
              return '<span class="rk-word">' + part + '</span>';
            })
            .join('');
        } else {
          // Keep nested element as-is, but wrap in word too for staggered effect
          return '<span class="rk-word rk-word--block">' + node.outerHTML + '</span>';
        }
      }).join('');
      h.innerHTML = newHtml;
    });

    // Observe each heading; when it enters viewport, stagger words in
    const headingTargets = document.querySelectorAll('.rk-video-feature__heading, .rk-video-hero__heading');
    if (!headingTargets.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const words = entry.target.querySelectorAll('.rk-word');
        words.forEach((w, i) => {
          w.style.transitionDelay = i * 60 + 'ms';
          w.classList.add('is-visible');
        });
        io.unobserve(entry.target);
      });
    }, { threshold: 0.25 });
    headingTargets.forEach((h) => io.observe(h));
  }

  /* ---------- Scroll progress bar ---------- */
  function initScrollProgress() {
    if (document.getElementById('rk-scroll-progress')) return;
    const bar = document.createElement('div');
    bar.id = 'rk-scroll-progress';
    document.body.appendChild(bar);
    let ticking = false;
    function update() {
      const h = document.documentElement;
      const scrolled = h.scrollTop || document.body.scrollTop;
      const max = (h.scrollHeight - h.clientHeight) || 1;
      const pct = Math.min(100, (scrolled / max) * 100);
      bar.style.transform = 'scaleX(' + (pct / 100) + ')';
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ---------- Subtle parallax ---------- */
  function initParallax() {
    if (reduceMotion) return;
    const items = document.querySelectorAll('[data-rk-parallax]');
    if (!items.length) return;

    let ticking = false;

    function update() {
      const vh = window.innerHeight;
      items.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        const center = rect.top + rect.height / 2;
        const progress = (center - vh / 2) / vh; // -1 .. 1 ish
        const speed = parseFloat(el.dataset.rkParallax) || 0.18;
        el.style.transform = 'translate3d(0,' + progress * speed * 100 + 'px,0)';
      });
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ---------- Video hero: autoplay muted, pause when off-screen ---------- */
  /* Pick a random video from a pool (data-rk-video-pool="url||url||url") on load */
  function initVideoPool() {
    // Selection only — NO load()/play() here. The lazy engine below fetches
    // the file when the section approaches the viewport, so six HQ videos
    // don't all download at once on page load (that was the lag).
    document.querySelectorAll('video[data-rk-video-pool]').forEach((video) => {
      const list = (video.getAttribute('data-rk-video-pool') || '')
        .split('||').map((s) => s.trim()).filter(Boolean);
      if (list.length < 2) return;
      const pick = list[Math.floor(Math.random() * list.length)];
      const src = video.querySelector('source');
      if (src && src.getAttribute('src') !== pick) src.setAttribute('src', pick);
    });
  }

  function initVideoHero() {
    initVideoPool();
    // Lazy engine for ALL theme videos: start downloading ~600px before the
    // section scrolls into view, play when visible, pause when off screen.
    // Full resolution is kept — only the TIMING of the download changes.
    const videos = document.querySelectorAll(
      '.rk-video-hero video, [data-rk-video-hero] video, .rk-video-feature video, [data-rk-lazy-video] video'
    );
    if (!videos.length) return;

    videos.forEach((video) => {
      if (video.dataset.rkLazyInit === 'true') return;
      video.dataset.rkLazyInit = 'true';
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      video.removeAttribute('autoplay');

      let activated = false;
      function activate() {
        if (activated) return;
        activated = true;
        // promote data-src sources (about hero) and start the real fetch
        video.querySelectorAll('source[data-src]').forEach((s) => {
          if (!s.getAttribute('src')) s.setAttribute('src', s.getAttribute('data-src'));
        });
        video.preload = 'auto';
        try { video.load(); } catch (e) {}
      }
      const tryPlay = () => {
        activate();
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      };

      // Pre-fetch margin: begin downloading well before the user arrives so the
      // clip is buffered and plays smoothly (no stutter/lag on scroll-in).
      const pre = new IntersectionObserver((es) => {
        es.forEach((e) => { if (e.isIntersecting) { activate(); pre.disconnect(); } });
      }, { rootMargin: '1600px 0px' });
      pre.observe(video);

      // Play / pause with actual visibility
      const io = new IntersectionObserver((es) => {
        es.forEach((e) => { if (e.isIntersecting) tryPlay(); else video.pause(); });
      }, { threshold: 0.1 });
      io.observe(video);

      // Resume on first user gesture if autoplay was blocked
      const resumeOnce = () => { if (activated) tryPlay(); };
      document.addEventListener('touchstart', resumeOnce, { passive: true, once: true });
      document.addEventListener('click', resumeOnce, { once: true });
    });
  }

  /* ---------- On-listing variant picker (color thumbs + size pills) ----------
     Click a color thumb or size pill on a card and:
       - mark it active (+ sibling state)
       - resolve the matching variant from embedded JSON
       - swap the card image to that variant
       - update the rk-buy-now form's hidden variant ID input
       - re-aim card product links (image + title) to ?variant=ID
     No navigation — stays on the listing. */
  function initCardVariantPicker() {
    document.querySelectorAll('.product-card-wrapper').forEach((card) => {
      if (card.dataset.rkVarInit === 'true') return;   // guard against double-init
      const dataEl = card.querySelector('script[type="application/json"][data-rk-variant-data]');
      if (!dataEl) return;
      let variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      if (!Array.isArray(variants) || !variants.length) return;
      card.dataset.rkVarInit = 'true';

      const mainImg = card.querySelector('.card__media img');
      const buyInput = card.querySelector('[data-rk-variant-input]');
      const buyForm = card.querySelector('[data-rk-buy-form]');
      const colorBtns = Array.from(card.querySelectorAll('.rk-thumb[data-rk-color]'));
      const sizeBtns = Array.from(card.querySelectorAll('.rk-sizes__item[data-rk-size]'));
      const productLinks = Array.from(card.querySelectorAll('a[href*="/products/"]'))
        .filter((a) => !a.closest('.rk-thumbs') && !a.closest('.rk-sizes'));

      const originalSrc = mainImg ? (mainImg.currentSrc || mainImg.src) : '';
      const originalSrcset = mainImg ? (mainImg.getAttribute('srcset') || '') : '';
      const baseHrefs = new Map();
      productLinks.forEach((a) => baseHrefs.set(a, a.getAttribute('href')));

      const hasColor = variants.some((v) => v.color !== null);
      const hasSize  = variants.some((v) => v.size  !== null);
      const productBaseUrl = (variants[0].url || '').split('?')[0];
      const firstAvail = variants.find((v) => v.available) || null;

      // Initial selection = the active buttons (Liquid sets is-active on the
      // default = first AVAILABLE variant, so this starts on something buyable)
      const activeColorBtn = colorBtns.find((b) => b.classList.contains('is-active'));
      const activeSizeBtn  = sizeBtns.find((b)  => b.classList.contains('is-active'));
      let selectedColor = activeColorBtn ? activeColorBtn.dataset.rkColor : (hasColor ? (firstAvail ? firstAvail.color : variants[0].color) : null);
      let selectedSize  = activeSizeBtn  ? activeSizeBtn.dataset.rkSize  : (hasSize  ? (firstAvail ? firstAvail.size  : variants[0].size)  : null);

      // Find the exact variant for a color+size combo (or null if it doesn't exist)
      function findExact(color, size) {
        return variants.find((v) =>
          (!hasColor || v.color === color) &&
          (!hasSize  || v.size  === size)
        ) || null;
      }

      // landingUrl/landingVariant updated by render(); used by the Buy Now submit
      let landingUrl = productBaseUrl;

      function render(swapImage) {
        const exact = findExact(selectedColor, selectedSize);
        const usable = (exact && exact.available) ? exact : null; // buyable combo
        // The variant we'll actually link the card to: the buyable combo, else
        // the first available variant (so the PDP add-to-cart is clickable),
        // else the first variant (whole product sold out).
        const landing = usable || firstAvail || variants[0];
        landingUrl = landing ? landing.url : productBaseUrl;

        // Only swap the card image when the user picks a colour/size.
        // On initial load we KEEP the Shopify-chosen featured image.
        if (swapImage) {
          const colorImg = (exact && exact.image) ||
            (hasColor ? (variants.find((v) => v.color === selectedColor && v.image) || {}).image : null) ||
            (landing && landing.image);
          if (mainImg && colorImg) {
            mainImg.removeAttribute('srcset');
            mainImg.src = colorImg;
          }
        }

        // Point all card links (image + title) at the landing variant
        productLinks.forEach((a) => a.setAttribute('href', landingUrl));

        // Buy Now: if the selected combo is buyable → checkout that variant;
        // otherwise → just open the product page (no forced sold-out variant).
        if (buyForm) {
          if (usable) {
            buyForm.dataset.rkMode = 'checkout';
            buyForm.removeAttribute('aria-disabled');
            if (buyInput) buyInput.value = usable.id;
          } else {
            buyForm.dataset.rkMode = 'pdp';
            // not "disabled" — it still works, it just routes to the PDP
            buyForm.removeAttribute('aria-disabled');
          }
        }

        // Mark size buttons that don't exist / are sold out FOR THE CHOSEN COLOUR
        if (hasSize) {
          sizeBtns.forEach((b) => {
            const sz = b.dataset.rkSize;
            const v = findExact(selectedColor, sz);
            const ok = v && v.available;
            b.classList.toggle('rk-sizes__item--unavailable', !ok);
            b.setAttribute('aria-disabled', ok ? 'false' : 'true');
          });
        }

        // Keep the highlighted colour/size in sync with the actual selection
        colorBtns.forEach((b) => {
          const on = b.dataset.rkColor === selectedColor;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        sizeBtns.forEach((b) => {
          const on = b.dataset.rkSize === selectedSize;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }

      function setActive(group, btn) {
        group.forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }

      let committed = false; // has the user actually picked a colour/size?

      // Reconcile the OTHER axis so the selection is always a buyable combo when
      // one exists (fixes sparse colour×size matrices, e.g. lehenga/men/kids).
      function firstAvailSizeForColor(color) {
        const v = variants.find((x) => x.color === color && x.available) ||
                  variants.find((x) => x.color === color);
        return v ? v.size : selectedSize;
      }
      function firstAvailColorForSize(size) {
        const v = variants.find((x) => x.size === size && x.available) ||
                  variants.find((x) => x.size === size);
        return v ? v.color : selectedColor;
      }

      colorBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          selectedColor = btn.dataset.rkColor;
          committed = true;
          if (hasSize) {
            const ex = findExact(selectedColor, selectedSize);
            if (!ex || !ex.available) selectedSize = firstAvailSizeForColor(selectedColor);
          }
          render(true);
        });
        /* Hover preview removed — image only changes on click, not on hover */
      });

      sizeBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          selectedSize = btn.dataset.rkSize;
          committed = true;
          if (hasColor) {
            const ex = findExact(selectedColor, selectedSize);
            if (!ex || !ex.available) selectedColor = firstAvailColorForSize(selectedSize);
          }
          render(true);
        });
      });

      // Buy Now submit. Decide the variant to buy:
      //  1) the selected colour/size combo if it's available, else
      //  2) whatever available variant the hidden input already holds (default),
      //     else 3) the first available variant.
      // If we have ANY available variant → check out. Only divert to the PDP
      // when the whole product is sold out.
      if (buyForm) {
        buyForm.addEventListener('submit', (e) => {
          const exact = findExact(selectedColor, selectedSize);
          let target = (exact && exact.available) ? exact : null;
          if (!target && buyInput) {
            const cur = variants.find((v) => String(v.id) === String(buyInput.value));
            if (cur && cur.available) target = cur;
          }
          if (!target) target = firstAvail || null;

          if (target) {
            if (buyInput) buyInput.value = target.id;   // → POST /cart/add → /checkout
          } else {
            e.preventDefault();
            window.location.href = landingUrl || productBaseUrl; // entirely sold out → PDP
          }
        });
      }

      card.addEventListener('mouseleave', () => {
        if (!mainImg) return;
        if (committed) {
          // Restore the committed selection's image
          const exact = findExact(selectedColor, selectedSize);
          const v = exact || firstAvail || variants[0];
          if (v && v.image) {
            mainImg.removeAttribute('srcset');
            mainImg.src = v.image;
            return;
          }
        }
        // No selection committed → restore the original Shopify featured image
        if (originalSrcset) mainImg.setAttribute('srcset', originalSrcset);
        mainImg.src = originalSrc;
      });

      render(false); // initial pass — links/sizes/buy mode only; keep featured image
    });
  }

  /* ---------- Swatch / thumbnail hover → swap card image + update card link ---------- */
  function initSwatchImageSwap() {
    document.querySelectorAll('.product-card-wrapper').forEach((card) => {
      // The full variant picker (cards with embedded variant JSON) already
      // handles image swap + link retargeting + availability. Skip those here
      // so the two systems don't fight over the links.
      if (card.querySelector('script[data-rk-variant-data]')) return;
      const triggers = card.querySelectorAll('[data-rk-image]');
      if (!triggers.length) return;

      const mainImg = card.querySelector('.card__media img');
      if (!mainImg) return;

      const originalSrc = mainImg.currentSrc || mainImg.src;
      const originalSrcset = mainImg.getAttribute('srcset') || '';

      // The main card link wraps the image (and usually the title). Targeting
      // all anchors that go to the product page lets us re-aim them at the
      // hovered variant so an image click lands on that exact variant.
      const cardLinks = Array.from(card.querySelectorAll('a[href*="/products/"]'))
        .filter((a) => !a.closest('.rk-thumbs') && !a.closest('.rk-swatches'));
      const originalHrefs = new Map();
      cardLinks.forEach((a) => originalHrefs.set(a, a.getAttribute('href')));

      const swap = (url) => {
        if (!url) return;
        mainImg.removeAttribute('srcset');
        mainImg.src = url;
      };
      const restore = () => {
        if (originalSrcset) mainImg.setAttribute('srcset', originalSrcset);
        mainImg.src = originalSrc;
        cardLinks.forEach((a) => {
          const original = originalHrefs.get(a);
          if (original) a.setAttribute('href', original);
        });
      };
      const retarget = (variantHref) => {
        if (!variantHref) return;
        cardLinks.forEach((a) => a.setAttribute('href', variantHref));
      };

      triggers.forEach((tr) => {
        const onActive = () => {
          swap(tr.dataset.rkImage);
          retarget(tr.getAttribute('href'));
        };
        tr.addEventListener('mouseenter', onActive);
        tr.addEventListener('focus', onActive);
      });

      card.addEventListener('mouseleave', restore);
    });
  }

  /* ---------- Hover-play product card videos ---------- */
  function initCardHoverVideos() {
    if (reduceMotion) return;
    const cards = document.querySelectorAll('.product-card-wrapper');
    cards.forEach((card) => {
      const video = card.querySelector('video[data-rk-card-video]');
      if (!video) return;
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      video.preload = 'metadata';

      card.addEventListener('mouseenter', () => {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      });
      card.addEventListener('mouseleave', () => {
        video.pause();
        try { video.currentTime = 0; } catch (e) { /* noop */ }
      });
    });
  }

  /* ---------- PDP color swatches: paint Color option pills with real colors ---------- */
  function rkColorFromName(raw) {
    if (!raw) return null;
    const n = String(raw).toLowerCase().trim();
    // Compound matches first
    if (n.includes('dark green'))   return '#1A4D2E';
    if (n.includes('light green'))  return '#90C695';
    if (n.includes('olive green'))  return '#6B7A3A';
    if (n.includes('sea green'))    return '#2E8B57';
    if (n.includes('emerald'))      return '#106F50';
    if (n.includes('teal'))         return '#0E7C7B';
    if (n.includes('pistachio'))    return '#A8C49A';
    if (n.includes('mint'))         return '#9CD7B3';
    if (n.includes('sage'))         return '#9CAF88';
    if (n.includes('greenish') || n.includes('grayish green')) return '#7A8B6C';

    if (n.includes('navy') || n.includes('dark blue')) return '#1A2C5A';
    if (n.includes('royal blue'))   return '#234FA8';
    if (n.includes('sky blue') || n.includes('light blue')) return '#A6CEE3';
    if (n.includes('turquoise'))    return '#3FBFB3';

    if (n.includes('maroon') || n.includes('wine') || n.includes('burgundy')) return '#7A1F2C';
    if (n.includes('crimson') || n.includes('ruby')) return '#B22335';
    if (n.includes('dark red'))     return '#7A1010';
    if (n.includes('red'))          return '#C9302C';

    if (n.includes('rose gold'))    return '#B76E79';
    if (n.includes('rose') || n.includes('pink') || n.includes('blush')) return '#E8A8B8';
    if (n.includes('magenta') || n.includes('fuchsia')) return '#C2185B';
    if (n.includes('mauve') || n.includes('lilac') || n.includes('lavender')) return '#B8A1CC';
    if (n.includes('purple') || n.includes('violet')) return '#6A2A8C';
    if (n.includes('plum'))         return '#693C5C';

    if (n.includes('orange'))       return '#E66B2A';
    if (n.includes('peach') || n.includes('apricot')) return '#F5C0A0';
    if (n.includes('coral'))        return '#F08080';
    if (n.includes('rust') || n.includes('terracotta')) return '#B5523A';

    if (n.includes('gold'))         return '#B89968';
    if (n.includes('mustard') || n.includes('ochre')) return '#C9A226';
    if (n.includes('yellow'))       return '#E5C100';

    if (n.includes('brown') || n.includes('chocolate') || n.includes('coffee')) return '#5D3A1A';
    if (n.includes('beige') || n.includes('tan') || n.includes('taupe') || n.includes('sand')) return '#D2B48C';

    if (n.includes('silver'))       return '#A8A8A8';
    if (n.includes('grey') || n.includes('gray')) return '#A8A8A8';
    if (n.includes('charcoal'))     return '#363636';

    if (n.includes('cream') || n.includes('ivory') || n.includes('off-white') || n.includes('off white')) return '#F4ECD9';
    if (n.includes('white'))        return '#FFFFFF';
    if (n.includes('black'))        return '#0E0E0E';

    if (n.includes('green'))        return '#2E7D32';
    if (n.includes('blue'))         return '#234FA8';
    return null;
  }

  function isColorOptionName(name) {
    if (!name) return false;
    const n = String(name).toLowerCase();
    return n.includes('color') || n.includes('colour') || n.includes('farbe');
  }

  function rkContrastText(hex) {
    if (!hex) return '#0E0E0E';
    const c = hex.replace('#', '');
    if (c.length !== 6) return '#0E0E0E';
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq > 150 ? '#0E0E0E' : '#FFFFFF';
  }

  /* ---------- PDP: default to the first AVAILABLE variant ----------
     Dawn loads with the first option combo selected; if that's sold out the
     Add to Cart button is disabled (grey). This selects the first available
     variant on load so the button is enabled (black) from the start. */
  function initPDPDefaultAvailable() {
    const vs = document.querySelector('variant-selects, variant-radios');
    if (!vs) return;

    // Locate the variant data JSON (array with .available + .option1)
    let variants = null;
    document.querySelectorAll('script[type="application/json"]').forEach((s) => {
      if (variants) return;
      try {
        const data = JSON.parse(s.textContent);
        if (Array.isArray(data) && data.length && 'available' in data[0] && 'option1' in data[0]) {
          variants = data;
        }
      } catch (e) {}
    });
    if (!variants) return;

    const firstAvail = variants.find((v) => v.available);
    if (!firstAvail) return; // whole product sold out → leave as-is

    function run() {
      const addBtn = document.querySelector('product-form button[name="add"], .product-form__submit');
      const disabled = addBtn && (addBtn.disabled || addBtn.hasAttribute('disabled'));
      if (!disabled) return; // default already available → nothing to do

      const fieldsets = vs.querySelectorAll('fieldset');
      const opts = [firstAvail.option1, firstAvail.option2, firstAvail.option3];
      let changed = false;
      fieldsets.forEach((fs, idx) => {
        const val = opts[idx];
        if (!val) return;
        const radios = fs.querySelectorAll('input[type="radio"]');
        radios.forEach((r) => {
          if (r.value === val && !r.checked) { r.checked = true; changed = true; }
          else if (r.value !== val && r.checked) { r.checked = false; }
        });
        // Support <select>-based variant pickers too
        const sel = fs.querySelector('select');
        if (sel && sel.value !== val) { sel.value = val; changed = true; }
      });
      if (changed) {
        const trigger = vs.querySelector('input[type="radio"]:checked') || vs.querySelector('select');
        if (trigger) trigger.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Belt & suspenders: Dawn re-enables the button via a section-render
      // fetch which can fail/lag. Since we KNOW firstAvail is buyable, sync
      // the form id + button state directly so Add to Cart is never stuck grey.
      const idInput = document.querySelector('product-form form input[name="id"], form[action*="/cart/add"] input[name="id"]');
      if (idInput) idInput.value = firstAvail.id;
      if (addBtn) {
        addBtn.removeAttribute('disabled');
        addBtn.disabled = false;
        addBtn.classList.remove('disabled');
        const label = addBtn.querySelector('span');
        const txt = (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
        if (label && /sold\s*out|ausverkauft|nicht verf/i.test(label.textContent)) label.textContent = txt;
      }
    }
    // Run after Dawn's variant pickers set their initial disabled state,
    // and once more later in case a section re-render restored the grey state.
    setTimeout(run, 60);
    setTimeout(run, 900);
  }

  function initPDPColorSwatches() {
    document.querySelectorAll('variant-selects, fieldset.product-form__input').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend');
      const optionName = legend ? legend.textContent : '';
      if (!isColorOptionName(optionName)) return;

      fieldset.classList.add('rk-pdp-color-fieldset');
      const pills = [];
      fieldset.querySelectorAll('label').forEach((label) => {
        const forId = label.getAttribute('for');
        let value = '';
        if (forId) {
          const input = document.getElementById(forId);
          if (input && input.value) value = input.value;
        }
        if (!value) value = label.textContent.trim();
        // Every colour label gets the rk pill style (consistent font/size),
        // whether or not its name maps to a known hex.
        label.classList.add('rk-pdp-color-pill');
        const css = rkColorFromName(value);
        if (css) {
          label.style.setProperty('--rk-pill-color', css);
          label.style.setProperty('--rk-pill-text', rkContrastText(css));
        }
        pills.push(label);
      });

      // Make EVERY pill the width of the widest one (e.g. "Bright Maroon"),
      // so they're all uniform regardless of how long the colour name is.
      function equalizePills() {
        if (!pills.length) return;
        pills.forEach((p) => { p.style.width = 'auto'; });
        let max = 0;
        pills.forEach((p) => { max = Math.max(max, p.offsetWidth); });
        if (max > 0) pills.forEach((p) => { p.style.width = max + 'px'; });
      }
      // Run now + after fonts load (font swap changes widths)
      equalizePills();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(equalizePills);
      }
      window.addEventListener('resize', equalizePills);
    });
  }

  /* ---------- Cursor follower ---------- */
  function initCursor() {
    if (reduceMotion) return;
    if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
    if (document.querySelector('.rk-cursor')) return;

    const cursor = document.createElement('div');
    cursor.className = 'rk-cursor';
    document.body.appendChild(cursor);

    let tx = -100, ty = -100, cx = -100, cy = -100;
    let raf = null;

    function loop() {
      cx += (tx - cx) * 0.22;
      cy += (ty - cy) * 0.22;
      cursor.style.transform = 'translate3d(' + (cx - 6) + 'px,' + (cy - 6) + 'px,0)';
      raf = requestAnimationFrame(loop);
    }

    document.addEventListener('mousemove', (e) => {
      tx = e.clientX;
      ty = e.clientY;
      cursor.classList.add('is-visible');
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });

    document.addEventListener('mouseleave', () => {
      cursor.classList.remove('is-visible');
    });

    const hoverSelectors = 'a, button, [role="button"], .rk-swatch, .rk-pdp-color-pill, .rk-sizes__item, .product-card-wrapper';
    document.addEventListener('mouseover', (e) => {
      if (e.target.closest(hoverSelectors)) {
        cursor.classList.add('is-hovering');
      }
    });
    document.addEventListener('mouseout', (e) => {
      if (e.target.closest(hoverSelectors)) {
        cursor.classList.remove('is-hovering');
      }
    });
  }

  /* ---------- Smooth-scroll for in-page anchors ---------- */
  function initSmoothAnchors() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]:not([href="#"])');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  /* ---------- Auto-apply reveal to homepage feature sections ---------- */
  function autoTagHomepageReveals() {
    if (!document.body.classList.contains('template-index') &&
        !document.body.classList.contains('template-home') &&
        document.body.getAttribute('data-template') !== 'index') {
      // Best-effort: also detect homepage by URL
      if (window.location.pathname !== '/' &&
          window.location.pathname !== '/en' &&
          window.location.pathname !== '/de') {
        return;
      }
    }
    const sections = document.querySelectorAll(
      '.rk-section-video-feature, .rk-section-video-hero ~ .shopify-section'
    );
    sections.forEach((s) => {
      if (!s.hasAttribute('data-rk-reveal') && !s.classList.contains('rk-reveal')) {
        s.classList.add('rk-reveal');
      }
    });
  }

  /* ---------- Sticky header — transparent → frosted shrink on scroll ---------- */
  function initStickyHeader() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;
    const threshold = 24;
    function update() {
      if (window.scrollY > threshold) header.classList.add('rk-header-scrolled');
      else header.classList.remove('rk-header-scrolled');
    }
    update();
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { update(); ticking = false; });
    }, { passive: true });
  }

  /* ---------- Magnetic buttons — cursor pulls element slightly ---------- */
  function initMagneticButtons() {
    if (reduceMotion) return;
    if (window.matchMedia('(hover: none)').matches) return;
    const targets = document.querySelectorAll('[data-rk-magnetic], .rk-cta-primary');
    const RADIUS = 60;
    const STRENGTH = 0.10;
    targets.forEach((el) => {
      let raf = 0;
      function move(e) {
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.hypot(dx, dy);
        if (dist > RADIUS) {
          reset();
          return;
        }
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.style.transform = `translate(${dx * STRENGTH}px, ${dy * STRENGTH}px)`;
        });
      }
      function reset() {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.style.transform = '';
        });
      }
      document.addEventListener('mousemove', move, { passive: true });
      el.addEventListener('mouseleave', reset);
    });
  }

  /* ---------- Horizontal collection rail: parallax inside cards + progress ---------- */
  function initCollectionRails() {
    if (reduceMotion) return;
    const rails = document.querySelectorAll('.rk-rail__track');
    rails.forEach((track) => {
      const cards = track.querySelectorAll('.rk-rail__card');
      const progress = track.parentElement.querySelector('.rk-rail__progress-bar');
      let raf = 0;
      function update() {
        cards.forEach((card) => {
          const img = card.querySelector('.rk-rail__card-media img');
          if (!img) return;
          const r = card.getBoundingClientRect();
          const center = window.innerWidth / 2;
          const offset = (r.left + r.width / 2 - center) / window.innerWidth;
          // Inner image drifts up to ~24px against the card direction
          img.style.setProperty('--rk-rail-x', `${-offset * 24}px`);
        });
        if (progress) {
          const max = track.scrollWidth - track.clientWidth;
          const pct = max > 0 ? (track.scrollLeft / max) * 100 : 0;
          progress.style.width = pct + '%';
        }
      }
      function onScroll() {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(update);
      }
      track.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    });
  }

  /* ---------- Pin scroll section (Phenomenon-style) ---------- */
  function initPinScroll() {
    if (reduceMotion) return;
    const sections = document.querySelectorAll('[data-rk-pin]');
    if (!sections.length) return;

    sections.forEach((section) => {
      const sticky = section.querySelector('.rk-pin__sticky');
      const medias = section.querySelectorAll('[data-rk-pin-media]');
      const panels = section.querySelectorAll('[data-rk-pin-panel]');
      if (!sticky || !medias.length) return;

      const count = medias.length;
      // Match track height to count: each panel gets 100vh of scroll
      section.style.minHeight = (count * 100) + 'vh';

      function update() {
        const rect = section.getBoundingClientRect();
        const total = section.offsetHeight - window.innerHeight;
        const progressed = Math.min(Math.max(-rect.top, 0), total);
        const ratio = total > 0 ? progressed / total : 0;
        // Active panel index based on ratio
        const active = Math.min(count - 1, Math.floor(ratio * count + 0.0001));
        medias.forEach((m, i) => m.classList.toggle('is-active', i === active));
        panels.forEach((p, i) => p.classList.toggle('is-active', i === active));
      }

      let ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { update(); ticking = false; });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    });
  }

  /* ---------- Hero: scroll-driven small logo rotation ---------- */
  function initHeroLogoScroll() {
    const heroes = document.querySelectorAll('[data-rk-hero-rotor]');
    if (!heroes.length) return;
    const DEG_PER_PX = 0.45; // refined: subtler than the old big-logo spin
    let ticking = false;
    function update() {
      const y = window.scrollY || window.pageYOffset || 0;
      heroes.forEach((h) => {
        h.style.setProperty('--rk-logo-rot', (y * DEG_PER_PX) + 'deg');
      });
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /* ---------- Whole card clickable → go to the (selected) product ----------
     Clicking anywhere on the card that isn't an interactive control navigates
     to the product. Respects the variant the user picked (rk-card-overlay href
     is kept in sync by initCardVariantPicker). */
  function initCardClickThrough() {
    document.querySelectorAll('.product-card-wrapper').forEach((card) => {
      if (card.dataset.rkClickInit === 'true') return;
      card.dataset.rkClickInit = 'true';
      card.style.cursor = 'pointer';
      card.addEventListener('click', (e) => {
        // Ignore clicks on real interactive elements
        if (e.target.closest('a, button, input, select, label, form, .rk-thumb, .rk-buy-now, .rk-buy-now-form, .rk-card__buy-wrap, .rk-thumbs, .rk-thumbs-slider, .rk-sizes__item, .quick-add')) {
          return;
        }
        // Prefer the variant-aware overlay link, else the title link
        const overlay = card.querySelector('a.rk-card-overlay');
        const titleLink = card.querySelector('.card__heading a, a[href*="/products/"]');
        const href = (overlay && overlay.getAttribute('href')) ||
                     (titleLink && titleLink.getAttribute('href'));
        if (href) window.location.href = href;
      });
    });
  }

  /* ---------- Card thumb row: inject a glassy slide bar when overflowing ----------
     - Slide bar sits at the bottom of .rk-thumbs-wrap
     - Track + draggable thumb mirror the scroll position of .rk-thumbs
     - Click track to jump, drag thumb to scroll
     - Native scroll/swipe still works in tandem
     - Visible when row physically overflows OR product has 4+ thumbs */
  function initThumbsScroller() {
    document.querySelectorAll('.rk-thumbs').forEach((row) => {
      if (row.dataset.rkScrollInit === 'true') return;
      row.dataset.rkScrollInit = 'true';

      // Wrap the thumb row in a relative container
      const wrap = document.createElement('div');
      wrap.className = 'rk-thumbs-wrap';
      row.parentNode.insertBefore(wrap, row);
      wrap.appendChild(row);

      function checkOverflow() {
        const thumbCount = row.querySelectorAll('.rk-thumb').length;
        const physicalOverflow = row.scrollWidth > row.clientWidth + 4;
        wrap.classList.toggle('has-overflow', physicalOverflow || thumbCount > 3);
      }
      function syncFromScroll() { /* slider removed — drag-to-scroll only */ }

      /* ----- Drag-to-scroll the THUMBNAIL ROW directly (grab the images) ----- */
      let down = false;        // pointer is down on the row
      let moved = false;       // moved past the click threshold
      let startX = 0;
      let startScroll = 0;
      const THRESHOLD = 10;    // px before a press counts as a drag (not a click)

      row.addEventListener('pointerdown', (e) => {
        // Only primary button / touch / pen
        if (e.button && e.button !== 0) return;
        down = true;
        moved = false;
        startX = e.clientX;
        startScroll = row.scrollLeft;
      });
      row.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > THRESHOLD) {
          moved = true;
          row.classList.add('is-dragging');
          // NOTE: do NOT setPointerCapture — it steals the click from the
          // thumbnail button, breaking colour selection on many-variant cards.
        }
        if (moved) {
          row.scrollLeft = startScroll - dx;
          e.preventDefault();
        }
      });
      function endPointer(e) {
        if (!down) return;
        down = false;
        if (moved) {
          // Suppress the click that follows a drag so it doesn't select a variant
          row.classList.remove('is-dragging');
          const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
          row.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(() => row.removeEventListener('click', swallow, true), 50);
        }
        moved = false;
      }
      row.addEventListener('pointerup', endPointer);
      row.addEventListener('pointercancel', endPointer);
      row.addEventListener('pointerleave', endPointer);

      row.addEventListener('scroll', syncFromScroll, { passive: true });
      // Wheel on the thumb row → horizontal scroll (vertical wheel converts to horizontal)
      row.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          row.scrollLeft += e.deltaY;
          e.preventDefault();
        }
      }, { passive: false });

      checkOverflow();
      syncFromScroll();

      // The wrap starts at max-height: 0 so initial sizing is wrong.
      // Re-sync whenever its size changes (e.g. when hover expands it).
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => {
          checkOverflow();
          syncFromScroll();
        });
        ro.observe(wrap);
        ro.observe(row);
      }

      // Also re-sync on first hover of the parent card (belt-and-suspenders)
      const card = row.closest('.product-card-wrapper');
      if (card) {
        const onceHover = () => {
          checkOverflow();
          syncFromScroll();
          card.removeEventListener('mouseenter', onceHover);
        };
        card.addEventListener('mouseenter', onceHover);
        card.addEventListener('focusin', onceHover, { once: true });
      }

      window.addEventListener('resize', () => { checkOverflow(); syncFromScroll(); });
    });
  }

  /* ---------- Lookbook: periodically swap a random tile's image ----------
     Every few seconds a random tile cross-fades to a different model photo,
     so the gallery feels alive and ever-changing. */
  function initLookbookDynamic() {
    if (reduceMotion) return;
    document.querySelectorAll('[data-rk-lookbook], [data-rk-img-shuffle]').forEach((grid) => {
      const total = parseInt(grid.dataset.rkModelTotal, 10) || 59;
      const tiles = Array.from(grid.querySelectorAll('img'));
      if (tiles.length < 2) return;

      const SIZES = ['', 'is-tall', 'is-wide', 'is-big'];
      function pad(n) { return n < 10 ? '0' + n : '' + n; }
      function tileOf(img) {
        return img.closest('figure, a, .rk-hero__model-tile, .rk-lookbook__tile') || img.parentElement;
      }
      const tileEls = tiles.map(tileOf);
      // FLIP moves EVERY grid item (image tiles + quote tiles) so nothing snaps
      const flipEls = Array.prototype.filter.call(grid.children, (el) => el.nodeType === 1);

      // --- Plain image crossfade (frequent, GPU-smooth opacity — no layout) ---
      function swapImage() {
        const img = tiles[Math.floor(Math.random() * tiles.length)];
        const next = Math.floor(Math.random() * total) + 1;
        const newSrc = img.getAttribute('src').replace(/rk-model-\d+\.jpg/, 'rk-model-' + pad(next) + '.jpg');
        if (newSrc === img.getAttribute('src')) return;
        img.style.transition = 'opacity 0.7s cubic-bezier(.4,0,.2,1)';
        img.style.opacity = '0';
        const pre = new Image();
        pre.onload = () => {
          img.src = newSrc;
          requestAnimationFrame(() => { img.style.opacity = '1'; });
        };
        pre.src = newSrc;
      }

      // --- The ONLY animation: an animated bento (Checkmarx-style choreography).
      //     A single "spotlight" hero travels the grid. As the new hero GROWS,
      //     the old one shrinks and EVERY item reflows via FLIP — but the moves
      //     are STAGGERED by distance from the spotlight, so the grid ripples
      //     outward like a wave (the choreography). Spring easing + a hero
      //     crossfade that hides the resize. No other animation runs. ---
      const FLIP_MS = 0.8;
      const SPRING = 'cubic-bezier(.22,1,.36,1)';   // smooth spring, faint settle
      const STAGGER_MAX = 0.22;                       // s — ripple spread
      let morphing = false;
      let activeIdx = -1;
      function focusNext() {
        if (morphing) return;
        morphing = true;

        // FIRST: record every grid item's current rect
        const first = flipEls.map((t) => t.getBoundingClientRect());

        // move the spotlight: old hero shrinks, next tile becomes hero
        if (activeIdx >= 0) tileEls[activeIdx].classList.remove('is-big');
        activeIdx = (activeIdx + 1) % tileEls.length;
        const target = tileEls[activeIdx];
        const targetImg = tiles[activeIdx];
        target.classList.add('is-big');

        // hero crossfades during its own grow → the scale-distortion is hidden,
        // and the photo change happens as part of the same choreographed move
        const next = Math.floor(Math.random() * total) + 1;
        const newSrc = targetImg.getAttribute('src').replace(/rk-model-\d+\.jpg/, 'rk-model-' + pad(next) + '.jpg');
        const pre = new Image();
        pre.onload = () => {
          targetImg.style.transition = 'opacity 0.32s cubic-bezier(.4,0,.2,1)';
          targetImg.style.opacity = '0';
          setTimeout(() => {
            targetImg.src = newSrc;
            requestAnimationFrame(() => { targetImg.style.opacity = '1'; });
          }, 300);
        };
        pre.src = newSrc;

        // LAST: measure the new layout + the spotlight centre (ripple origin)
        const last = flipEls.map((t) => t.getBoundingClientRect());
        const cx = last[activeIdx] ? last[activeIdx].left + last[activeIdx].width / 2 : 0;
        const cy = last[activeIdx] ? last[activeIdx].top + last[activeIdx].height / 2 : 0;
        let maxDist = 1;
        const dist = last.map((r) => {
          const d = Math.hypot(r.left + r.width / 2 - cx, r.top + r.height / 2 - cy);
          if (d > maxDist) maxDist = d;
          return d;
        });

        // INVERT + PLAY, staggered by distance → outward ripple (choreography)
        flipEls.forEach((t, i) => {
          const dx = first[i].left - last[i].left;
          const dy = first[i].top - last[i].top;
          const sx = first[i].width  / last[i].width;
          const sy = first[i].height / last[i].height;
          const moved = Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5 ||
                        Math.abs(sx - 1) > 0.01 || Math.abs(sy - 1) > 0.01;
          if (!moved) { t.style.transition = ''; t.style.transform = ''; return; }
          const delay = (dist[i] / maxDist) * STAGGER_MAX;
          t.style.transformOrigin = 'top left';
          t.style.transition = 'none';
          t.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + sx + ',' + sy + ')';
          requestAnimationFrame(() => {
            t.style.transition = 'transform ' + FLIP_MS + 's ' + SPRING + ' ' + delay.toFixed(3) + 's';
            t.style.transform = '';
          });
        });

        setTimeout(() => { morphing = false; }, (FLIP_MS + STAGGER_MAX) * 1000 + 120);
      }

      // Only run while the section is on screen
      let visible = false;
      const io = new IntersectionObserver((es) => {
        es.forEach((e) => { visible = e.isIntersecting; });
      }, { threshold: 0.05 });
      io.observe(grid);
      // The spotlight travels tile-to-tile — choreographed move over the
      //     always-floating wall (the continuous float lives in CSS).
      setInterval(() => { if (visible && !document.hidden) focusNext(); }, 2200);
    });
  }

  /* ---------- Section background parallax — bg shifts as you scroll ---------- */
  function initSectionBgParallax() {
    if (reduceMotion) return;
    const sections = document.querySelectorAll('.rk-video-feature');
    if (!sections.length) return;
    const RANGE = 60; // px of vertical drift across the viewport pass
    let ticking = false;
    function update() {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      sections.forEach((sec) => {
        const r = sec.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return; // skip far-offscreen
        // progress: 0 when section enters bottom, 1 when it exits top
        const progress = (vh - r.top) / (vh + r.height);
        const y = (progress - 0.5) * 2 * RANGE; // -RANGE … +RANGE
        sec.style.setProperty('--rk-bg-y', y.toFixed(1) + 'px');
      });
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------- Video-feature bento mosaic: shuffle + assign sizes ----------
     Renders a random subset of the collection each load with varied tile sizes.
     CSS hides extras responsively (5 desktop / 4 tablet / 3 mobile). */
  function initVideoFeatureBento() {
    document.querySelectorAll('[data-rk-bento]').forEach((bento) => {
      if (bento.dataset.rkBentoInit === 'true') return;
      bento.dataset.rkBentoInit = 'true';

      const rail = bento.querySelector('.rk-vf-cards-rail') || bento;
      const cards = Array.from(rail.querySelectorAll('[data-rk-bento-card]'));
      if (cards.length < 2) return;

      // Fisher-Yates shuffle
      for (let k = cards.length - 1; k > 0; k--) {
        const j = Math.floor(Math.random() * (k + 1));
        [cards[k], cards[j]] = [cards[j], cards[k]];
      }
      // Re-append in shuffled order INTO THE RAIL (keeps mobile scroller intact)
      cards.forEach((c) => rail.appendChild(c));

      // Assign a varied size pattern so the mosaic is asymmetric.
      // Pattern repeats; shuffle means different products land in each size.
      const pattern = ['tall', 'small', 'small', 'wide', 'small', 'tall', 'small', 'small'];
      cards.forEach((card, i) => {
        card.classList.remove('rk-vf-card--tall', 'rk-vf-card--wide', 'rk-vf-card--big');
        const size = pattern[i % pattern.length];
        if (size === 'tall') card.classList.add('rk-vf-card--tall');
        else if (size === 'wide') card.classList.add('rk-vf-card--wide');
      });

      // ----- Mobile rail: dynamic fade + slide button -----
      if (rail !== bento) initRail(rail, bento);
    });
  }

  /* Mobile rail behaviour: cards entering from the right are dim and brighten
     as they scroll into view; a glassy slide button advances the rail. */
  function initRail(rail, bento) {
    const isMobile = window.matchMedia('(max-width: 749px)');
    const collectionUrl = bento.getAttribute('data-rk-collection-url') || '';

    // Wrap the rail in a relative container so the button can center on the cards
    let railWrap = rail.parentElement;
    if (!railWrap || !railWrap.classList.contains('rk-vf-rail-wrap')) {
      railWrap = document.createElement('div');
      railWrap.className = 'rk-vf-rail-wrap';
      rail.parentNode.insertBefore(railWrap, rail);
      railWrap.appendChild(rail);
    }

    // Inject the slide-right button once (inside the wrap → centers on cards)
    let nextBtn = railWrap.querySelector('.rk-vf-rail-next');
    if (!nextBtn) {
      nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'rk-vf-rail-next';
      nextBtn.setAttribute('aria-label', 'Show more products');
      nextBtn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>';
      railWrap.appendChild(nextBtn);
      nextBtn.addEventListener('click', () => {
        const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
        if (atEnd && collectionUrl) {
          // Already at the end → open the matching collection page
          window.location.href = collectionUrl;
          return;
        }
        const card = rail.querySelector('.rk-vf-card');
        const step = card ? card.getBoundingClientRect().width + 12 : rail.clientWidth * 0.5;
        rail.scrollBy({ left: step * 2, behavior: 'smooth' });
      });
    }

    function updateFade() {
      if (!isMobile.matches) {
        // Desktop: clear any inline opacity so the bento grid is unaffected
        rail.querySelectorAll('.rk-vf-card').forEach((c) => { c.style.opacity = ''; });
        nextBtn.style.display = 'none';
        return;
      }
      nextBtn.style.display = '';
      const railRect = rail.getBoundingClientRect();
      rail.querySelectorAll('.rk-vf-card').forEach((card) => {
        const r = card.getBoundingClientRect();
        const centerX = (r.left + r.width / 2) - railRect.left;
        const ratio = centerX / railRect.width;        // 0 = left edge, 1 = right edge
        let op = 1;
        if (ratio > 0.62) {
          // fade from 1 → 0.3 as the card sits further toward / past the right edge
          op = Math.max(0.3, 1 - (ratio - 0.62) / 0.55);
        } else if (ratio < 0.05) {
          // slight fade for cards scrolled off the left
          op = Math.max(0.45, 0.45 + ratio / 0.05 * 0.55);
        }
        card.style.opacity = op.toFixed(3);
      });
      // Hide the button when scrolled to the end
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      nextBtn.classList.toggle('is-end', atEnd);
    }

    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateFade(); ticking = false; });
    }
    rail.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateFade);
    isMobile.addEventListener('change', updateFade);
    // Initial pass (next frame so layout is settled)
    requestAnimationFrame(updateFade);
  }

  /* ---------- Quick-Buy popup (collection / grid cards) ----------
     Buy Now opens a glassy confirmation modal (hero-editorial styling)
     instead of submitting the form. This ALSO fixes the saree bug: the
     Optionize app intercepts /cart/add form submits and silently redirects
     to the product page — we capture the click before any submit happens
     and add to cart via fetch('/cart/add.js'), which apps can't intercept. */
  function initBuyNowPopup() {
    if (document.querySelector('.rk-qb')) return;

    // ----- modal skeleton (built once) -----
    const wrap = document.createElement('div');
    wrap.className = 'rk-qb';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML =
      '<div class="rk-qb__backdrop" data-rk-qb-close></div>' +
      '<div class="rk-qb__panel" role="dialog" aria-modal="true" aria-label="Quick buy">' +
        '<button type="button" class="rk-qb__close" data-rk-qb-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>' +
        '</button>' +
        '<div class="rk-qb__media"><img alt="" data-rk-qb-img></div>' +
        '<div class="rk-qb__body">' +
          '<p class="rk-qb__eyebrow">RUKKUMANI · ருக்குமணி</p>' +
          '<h3 class="rk-qb__title" data-rk-qb-title></h3>' +
          '<p class="rk-qb__variant" data-rk-qb-variant></p>' +
          '<p class="rk-qb__price" data-rk-qb-price></p>' +
          '<div class="rk-qb__trust">' +
            '<span>Free shipping in Switzerland</span><span>·</span><span>Delivery in 3–5 days</span>' +
          '</div>' +
          '<div class="rk-qb__actions">' +
            '<button type="button" class="rk-qb__buy" data-rk-qb-buy>' +
              '<span>Buy it now</span>' +
              '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="12" x2="20" y2="12"/><polyline points="14 6 20 12 14 18"/></svg>' +
            '</button>' +
            '<button type="button" class="rk-qb__continue" data-rk-qb-continue><span>Add &amp; continue shopping</span></button>' +
          '</div>' +
          '<p class="rk-qb__hint">You can review everything before paying.</p>' +
        '</div>' +
      '</div>';
    document.body.appendChild(wrap);

    const img = wrap.querySelector('[data-rk-qb-img]');
    const ttl = wrap.querySelector('[data-rk-qb-title]');
    const varEl = wrap.querySelector('[data-rk-qb-variant]');
    const priceEl = wrap.querySelector('[data-rk-qb-price]');
    const buyBtn = wrap.querySelector('[data-rk-qb-buy]');
    const contBtn = wrap.querySelector('[data-rk-qb-continue]');

    let current = null;   // { id, url }

    function openModal(data) {
      current = data;
      img.src = data.image || '';
      ttl.textContent = data.title || '';
      varEl.textContent = data.variantLabel || '';
      varEl.style.display = data.variantLabel ? '' : 'none';
      priceEl.textContent = data.price || '';
      buyBtn.classList.remove('is-busy'); contBtn.classList.remove('is-busy', 'is-done');
      contBtn.querySelector('span').innerHTML = 'Add &amp; continue shopping';
      wrap.classList.add('is-open');
      wrap.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('rk-qb-lock');
    }
    function closeModal() {
      wrap.classList.remove('is-open');
      wrap.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('rk-qb-lock');
    }
    wrap.querySelectorAll('[data-rk-qb-close]').forEach((el) => el.addEventListener('click', closeModal));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeModal(); });

    // Cart-app scripts (Optionize / Pop-Convert) monkey-patch window.fetch to
    // intercept /cart/add.js and redirect to the PDP. A fresh same-origin
    // iframe gives us the browser's PRISTINE fetch they never touched.
    let cleanWin = null;
    function getCleanFetch() {
      try {
        if (!cleanWin) {
          const fr = document.createElement('iframe');
          fr.style.display = 'none';
          fr.setAttribute('aria-hidden', 'true');
          document.documentElement.appendChild(fr);
          cleanWin = fr.contentWindow;
        }
        if (cleanWin && typeof cleanWin.fetch === 'function') return cleanWin.fetch.bind(cleanWin);
      } catch (e) {}
      return window.fetch.bind(window);
    }
    function addToCart(id) {
      const doFetch = getCleanFetch();
      return doFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: Number(id), quantity: 1 })
      }).then((r) => { if (!r.ok) throw new Error('add failed ' + r.status); return r.json(); });
    }
    function syncCartBubble() {
      fetch('/cart.js').then((r) => r.json()).then((cart) => {
        const icon = document.querySelector('#cart-icon-bubble');
        if (!icon) return;
        let bubble = icon.querySelector('.cart-count-bubble');
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          icon.appendChild(bubble);
        }
        bubble.innerHTML = '<span aria-hidden="true">' + cart.item_count + '</span>' +
          '<span class="visually-hidden">' + cart.item_count + ' items</span>';
      }).catch(() => {});
    }

    buyBtn.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();   // keep cart-apps' listeners out
      if (!current || buyBtn.classList.contains('is-busy')) return;
      buyBtn.classList.add('is-busy');
      addToCart(current.id)
        .then(() => { window.location.href = '/checkout'; })
        .catch(() => { window.location.href = current.url || '/cart'; });
    });
    contBtn.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      if (!current || contBtn.classList.contains('is-busy')) return;
      contBtn.classList.add('is-busy');
      addToCart(current.id)
        .then(() => {
          contBtn.classList.add('is-done');
          contBtn.querySelector('span').textContent = 'Added to your cart ✓';
          syncCartBubble();
          setTimeout(closeModal, 1100);
        })
        .catch(() => { window.location.href = current.url || '/cart'; });
    });

    // ----- intercept Buy Now clicks at CAPTURE phase (before any app) -----
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-rk-buy-form] .rk-buy-now');
      if (!btn) return;
      const card = btn.closest('.product-card-wrapper');
      if (!card) return;                       // PDP & other forms keep native flow
      e.preventDefault();
      e.stopPropagation();

      const input = card.querySelector('[data-rk-variant-input]');
      const id = input && input.value;
      if (!id) return;

      // details from the card's variant JSON + DOM
      let v = null;
      try {
        const data = JSON.parse(card.querySelector('script[data-rk-variant-data]').textContent);
        v = data.find((x) => String(x.id) === String(id)) || null;
      } catch (err) {}
      const heading = card.querySelector('.card__heading a, .card__heading');
      const cardImg = card.querySelector('.card__media img');
      const priceNode = card.querySelector('.price__sale .price-item--sale, .price-item--regular, .price-item');
      const label = v ? [v.color, v.size].filter(Boolean).join(' · ') : '';

      openModal({
        id,
        url: (v && v.url) || (heading && heading.getAttribute && heading.getAttribute('href')) || '/cart',
        title: heading ? heading.textContent.trim() : '',
        image: (v && v.image) || (cardImg ? (cardImg.currentSrc || cardImg.src) : ''),
        variantLabel: label,
        price: priceNode ? priceNode.textContent.trim() : ''
      });
    }, true);
  }

  /* ---------- About page: reveals + story deck ---------- */
  function initRkAbout() {
    const root = document.querySelector('[data-rk-about]');
    if (!root || root.dataset.rkInit === 'true') return;
    root.dataset.rkInit = 'true';

    // Scroll reveals (staggered via --rk-i)
    const items = root.querySelectorAll('.rk-about-reveal');
    if (reduceMotion) {
      items.forEach((el) => el.classList.add('is-in'));
    } else {
      const io = new IntersectionObserver((es) => {
        es.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      items.forEach((el) => io.observe(el));
    }

    // Story deck: arrows + drag-to-scroll + progress bar
    const deck = root.querySelector('[data-rk-deck]');
    if (!deck) return;
    const bar = root.querySelector('[data-rk-deck-bar]');
    const prev = root.querySelector('[data-rk-deck-prev]');
    const next = root.querySelector('[data-rk-deck-next]');
    const step = () => {
      const card = deck.querySelector('.rk-about__card');
      return card ? card.getBoundingClientRect().width + 20 : deck.clientWidth * 0.6;
    };
    if (prev) prev.addEventListener('click', () => deck.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => deck.scrollBy({ left: step(), behavior: 'smooth' }));

    function syncBar() {
      if (!bar) return;
      const max = deck.scrollWidth - deck.clientWidth;
      const pct = max > 0 ? (deck.scrollLeft / max) : 0;
      bar.style.width = (12 + pct * 88) + '%';
    }
    deck.addEventListener('scroll', () => requestAnimationFrame(syncBar), { passive: true });
    syncBar();

    // drag-to-scroll (desktop)
    let down = false, moved = false, sx = 0, ss = 0;
    deck.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return;       // native touch scroll
      down = true; moved = false; sx = e.clientX; ss = deck.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - sx;
      if (Math.abs(dx) > 6) { moved = true; deck.classList.add('is-dragging'); }
      if (moved) deck.scrollLeft = ss - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      deck.classList.remove('is-dragging');
      if (moved) {
        const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        deck.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => deck.removeEventListener('click', swallow, true), 60);
      }
    });
  }

  /* ---------- Hero model bento (Checkmarx-style choreography) ----------
     5 model blocks in fixed 3×3 areas. A focus travels block-to-block; the
     focused block's grid TRACKS expand, so the whole grid resizes and the
     lines slide. Only track sizes animate → buttery. The hero block also
     crossfades to a fresh model each time, for variety. */
  function initHeroBento() {
    const grid = document.querySelector('[data-rk-hero-bento]');
    if (!grid || grid.dataset.rkInit === 'true') return;
    grid.dataset.rkInit = 'true';

    const blocks = Array.prototype.slice.call(grid.querySelectorAll('.rk-hero__bento-block'));
    if (blocks.length < 2) return;
    const total = parseInt(grid.dataset.rkModelTotal, 10) || 59;
    const order = blocks.map((b) => b.getAttribute('data-area'));
    // Desktop/tablet: 6 cols × 6 rows, 15 blocks (a–o).
    const desktopMap = {
      a: { cols: [0], rows: [0, 1] },
      b: { cols: [0], rows: [2, 3] },
      c: { cols: [0], rows: [4, 5] },
      d: { cols: [1], rows: [0, 1, 2] },
      e: { cols: [1], rows: [3, 4, 5] },
      f: { cols: [2], rows: [0, 1] },
      g: { cols: [2], rows: [2, 3] },
      h: { cols: [2], rows: [4, 5] },
      i: { cols: [3], rows: [0, 1, 2] },
      j: { cols: [3], rows: [3, 4, 5] },
      k: { cols: [4], rows: [0, 1] },
      l: { cols: [4], rows: [2, 3] },
      m: { cols: [4], rows: [4, 5] },
      n: { cols: [5], rows: [0, 1, 2] },
      o: { cols: [5], rows: [3, 4, 5] }
    };
    // Phone: 3 cols × 6 rows, 7 blocks (a–g) — matches the mobile CSS areas.
    const phoneMap = {
      a: { cols: [0], rows: [0, 1] },
      b: { cols: [0], rows: [2, 3] },
      c: { cols: [0], rows: [4, 5] },
      d: { cols: [1], rows: [0, 1, 2] },
      e: { cols: [1], rows: [3, 4, 5] },
      f: { cols: [2], rows: [0, 1, 2] },
      g: { cols: [2], rows: [3, 4, 5] }
    };
    const phone = window.matchMedia('(max-width: 749px)');
    function cfg() {
      return phone.matches
        ? { map: phoneMap, ncols: 3, nrows: 6, count: 7 }
        : { map: desktopMap, ncols: 6, nrows: 6, count: 15 };
    }
    // Columns swell GENTLY (stay narrow → never wider than tall); rows swell more.
    const COL_HERO = 1.4, COL_REST = 0.88;
    const ROW_HERO = 1.7, ROW_REST = 0.86;

    const QUOTES = [
      'THE CULTURE YOU SEEK', 'ருக்குமணி', 'WOVEN IN TRADITION', 'DRAPED IN STORY',
      'CURATED IN BASEL', 'HERITAGE IN EVERY THREAD', 'சாறி · சல்வார் · லெஹெங்கா',
      'SHIPPED ACROSS SWITZERLAND'
    ];

    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function swapImg(img) {
      const next = Math.floor(Math.random() * total) + 1;
      const src = img.getAttribute('src').replace(/rk-model-\d+\.jpg/, 'rk-model-' + pad(next) + '.jpg');
      if (src === img.getAttribute('src')) return;
      const pre = new Image();
      pre.onload = () => {
        img.style.transition = 'opacity 0.5s cubic-bezier(.4,0,.2,1)';
        img.style.opacity = '0';
        setTimeout(() => { img.src = src; requestAnimationFrame(() => { img.style.opacity = '1'; }); }, 260);
      };
      pre.src = src;
    }
    const quoteBlocks = blocks.filter((b) => b.hasAttribute('data-rk-quote'));
    function swapQuote(block) {
      const span = block.querySelector('.rk-hero__bento-quote');
      if (!span) return;
      // never repeat a statement already shown in another quote block
      const inUse = quoteBlocks
        .filter((b) => b !== block)
        .map((b) => { const s = b.querySelector('.rk-hero__bento-quote'); return s ? s.textContent : ''; });
      let choices = QUOTES.filter((q) => q !== span.textContent && inUse.indexOf(q) < 0);
      if (!choices.length) choices = QUOTES.filter((q) => q !== span.textContent);
      if (!choices.length) return;
      const q = choices[Math.floor(Math.random() * choices.length)];
      span.style.transition = 'opacity 0.45s cubic-bezier(.4,0,.2,1)';
      span.style.opacity = '0';
      setTimeout(() => { span.textContent = q; requestAnimationFrame(() => { span.style.opacity = '1'; }); }, 240);
    }

    function focus(i, doSwap) {
      const c = cfg();
      const m = c.map[order[i]];
      if (!m) return;
      const cols = []; for (let x = 0; x < c.ncols; x++) cols.push((m.cols.indexOf(x) >= 0 ? COL_HERO : COL_REST) + 'fr');
      const rows = []; for (let y = 0; y < c.nrows; y++) rows.push((m.rows.indexOf(y) >= 0 ? ROW_HERO : ROW_REST) + 'fr');
      grid.style.gridTemplateColumns = cols.join(' ');
      grid.style.gridTemplateRows    = rows.join(' ');
      blocks.forEach((b, k) => b.classList.toggle('is-active', k === i));
      if (doSwap) {
        const b = blocks[i];
        if (b.hasAttribute('data-rk-quote')) swapQuote(b);
        else { const img = b.querySelector('img'); if (img) swapImg(img); }
      }
    }

    let visible = false;
    const io = new IntersectionObserver((es) => { es.forEach((e) => { visible = e.isIntersecting; }); }, { threshold: 0.05 });
    io.observe(grid);

    // a small LOCAL change — one random VISIBLE block swaps its photo / quote
    function localChange() {
      const k = Math.floor(Math.random() * cfg().count);
      const b = blocks[k];
      if (b.hasAttribute('data-rk-quote')) swapQuote(b);
      else { const img = b.querySelector('img'); if (img) swapImg(img); }
    }

    // Randomise the INITIAL photos so every page load shows a different, varied
    // mix from all model images (instead of always rk-model-01…11 in order).
    (function randomizeInitial() {
      const imgBlocks = blocks.filter((b) => !b.hasAttribute('data-rk-quote'));
      const pool = []; for (let n = 1; n <= total; n++) pool.push(n);
      for (let n = pool.length - 1; n > 0; n--) {        // Fisher–Yates shuffle
        const r = Math.floor(Math.random() * (n + 1));
        const t = pool[n]; pool[n] = pool[r]; pool[r] = t;
      }
      imgBlocks.forEach((b, idx) => {
        const img = b.querySelector('img');
        if (!img) return;
        const num = pool[idx % pool.length];
        const cur = img.getAttribute('src') || '';
        const src = cur.replace(/rk-model-\d+\.jpg/, 'rk-model-' + pad(num) + '.jpg');
        if (src && src !== cur) img.setAttribute('src', src);
      });
    })();

    let i = 0;
    focus(0, false);                              // initial state (desktop or phone)
    phone.addEventListener('change', () => { i = 0; focus(0, false); });

    if (reduceMotion) return;        // static if the visitor prefers reduced motion
    // Frequent, calm, LOCAL changes — only a small part changes at a time
    setInterval(() => { if (visible && !document.hidden) localChange(); }, 4000);
    // Keep the statements rotating too (only the visible quote blocks)
    if (quoteBlocks.length) {
      setInterval(() => {
        if (!visible || document.hidden) return;
        const vis = quoteBlocks.filter((b) => blocks.indexOf(b) < cfg().count);
        if (vis.length) swapQuote(vis[Math.floor(Math.random() * vis.length)]);
      }, 4500);
    }
    // STICKY full reflow — the focus travels (heavy & smooth)
    setInterval(() => {
      if (!visible || document.hidden) return;
      i = (i + 1) % cfg().count;
      focus(i, true);
    }, 5200);
  }

  /* ---------- Desktop: Bento grid → stacked-cards deck browser ----------
     "View collection" collapses the bento grid into a centred deck (FLIP morph);
     the front card can be dragged/flicked, and prev/next arrows cycle the full
     collection. Close returns to the grid. Desktop only (>=990px). */
  function initBentoStack() {
    const desktop = window.matchMedia('(min-width: 990px)');
    document.querySelectorAll('[data-rk-bento]').forEach((bento) => {
      const openBtn = bento.querySelector('[data-rk-stack-open]');
      const rail = bento.querySelector('.rk-vf-cards-rail');
      if (!openBtn || !rail) return;
      if (bento.dataset.rkStackInit === 'true') return;
      bento.dataset.rkStackInit = 'true';

      const ui = bento.querySelector('.rk-vf-stack-ui');
      const prevBtn = bento.querySelector('[data-rk-stack-prev]');
      const nextBtn = bento.querySelector('[data-rk-stack-next]');
      const closeBtn = bento.querySelector('[data-rk-stack-close]');
      const curEl = bento.querySelector('[data-rk-stack-cur]');
      const totalEl = bento.querySelector('[data-rk-stack-total]');

      let cards = [];
      let index = 0;
      let stacked = false;

      function freshCards() {
        return Array.prototype.slice.call(rail.querySelectorAll('.rk-vf-card'));
      }

      // deck transform for a card whose offset from the top card is p (=i-index)
      function deckStyle(card, p) {
        let t, o, z, pe;
        if (p === 0) { t = 'translate(0,0) scale(1) rotate(0deg)'; o = 1; z = 300; pe = 'auto'; }
        else if (p < 0) { t = 'translate(-165%,0) rotate(-9deg) scale(0.92)'; o = 0; z = 1; pe = 'none'; }
        else {
          const off = Math.min(p, 4);
          t = 'translate(0,' + (off * 16) + 'px) scale(' + (1 - off * 0.05).toFixed(3) + ')';
          o = p <= 3 ? 1 : 0; z = 300 - p; pe = 'none';
        }
        card.style.transform = t;
        card.style.opacity = String(o);
        card.style.zIndex = String(z);
        card.style.pointerEvents = pe;
      }

      function render(withTransition) {
        cards.forEach((card, i) => {
          card.style.transition = withTransition
            ? 'transform 0.6s cubic-bezier(.22,1,.36,1), opacity 0.45s ease'
            : 'none';
          deckStyle(card, i - index);
        });
        if (curEl) curEl.textContent = String(index + 1);
        if (prevBtn) prevBtn.disabled = index <= 0;
        if (nextBtn) nextBtn.disabled = index >= cards.length - 1;
      }

      function go(delta) {
        const ni = Math.min(Math.max(index + delta, 0), cards.length - 1);
        if (ni === index) { render(true); return; }
        index = ni;
        render(true);
      }

      // ---- open: FLIP from grid cells into the centred deck ----
      function open() {
        if (stacked || !desktop.matches) return;
        cards = freshCards();
        if (cards.length < 2) return;
        stacked = true;
        index = 0;
        if (totalEl) totalEl.textContent = String(cards.length);
        if (ui) ui.setAttribute('aria-hidden', 'false');

        const first = cards.map((c) => c.getBoundingClientRect());
        bento.classList.add('is-stacked');
        // measure the deck anchor (transform:none base position, same for all)
        cards.forEach((c) => { c.style.transition = 'none'; c.style.transform = 'none'; c.style.opacity = '0'; });
        const base = cards.map((c) => c.getBoundingClientRect());

        // invert: place each card visually back at its grid spot
        cards.forEach((c, i) => {
          const f = first[i], b = base[i];
          if (f.width > 0) {
            const dx = (f.left + f.width / 2) - (b.left + b.width / 2);
            const dy = (f.top + f.height / 2) - (b.top + b.height / 2);
            const s = f.width / b.width;
            c.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + s.toFixed(3) + ')';
            c.style.opacity = '1';
          } else {
            c.style.transform = 'scale(0.6)';
            c.style.opacity = '0';
          }
        });
        // play: animate from grid → deck
        requestAnimationFrame(() => { requestAnimationFrame(() => render(true)); });
        armScrollClose();
      }

      // ---- close: FLIP from deck back into the grid ----
      function close() {
        if (!stacked) return;
        stacked = false;
        window.removeEventListener('scroll', onScrollClose);
        if (ui) ui.setAttribute('aria-hidden', 'true');
        const first = cards.map((c) => c.getBoundingClientRect());
        cards.forEach((c) => { c.style.transition = 'none'; c.style.transform = ''; c.style.opacity = ''; c.style.zIndex = ''; c.style.pointerEvents = ''; });
        bento.classList.remove('is-stacked');
        const last = cards.map((c) => c.getBoundingClientRect());
        cards.forEach((c, i) => {
          const f = first[i], l = last[i];
          if (l.width === 0) return;             // hidden again in grid
          const dx = (f.left + f.width / 2) - (l.left + l.width / 2);
          const dy = (f.top + f.height / 2) - (l.top + l.height / 2);
          const s = f.width / l.width;
          c.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + s.toFixed(3) + ')';
        });
        requestAnimationFrame(() => {
          cards.forEach((c) => {
            c.style.transition = 'transform 0.55s cubic-bezier(.22,1,.36,1), opacity 0.4s ease';
            c.style.transform = '';
          });
        });
        // clean up inline styles after the animation
        setTimeout(() => {
          if (stacked) return;
          cards.forEach((c) => { c.style.transition = ''; c.style.transform = ''; });
        }, 650);
      }

      // ---- scrolling the page closes the deck, but only after the visitor
      //      scrolls a meaningful distance (not on the first tiny nudge) ----
      let scrollStartY = 0;
      const SCROLL_CLOSE = 160;   // px of travel before we unstack
      function onScrollClose() {
        if (!stacked) return;
        if (Math.abs(window.pageYOffset - scrollStartY) > SCROLL_CLOSE) close();
      }
      function armScrollClose() {
        setTimeout(() => {
          if (!stacked) return;
          scrollStartY = window.pageYOffset;
          window.addEventListener('scroll', onScrollClose, { passive: true });
        }, 450);
      }

      openBtn.addEventListener('click', open);
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (prevBtn) prevBtn.addEventListener('click', () => go(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => go(1));
      document.addEventListener('keydown', (e) => {
        if (!stacked) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') go(-1);
        else if (e.key === 'ArrowRight') go(1);
      });
      // leaving desktop while stacked → revert to grid
      desktop.addEventListener('change', () => { if (!desktop.matches && stacked) close(); });
    });
  }

  /* ---------- Hero focal carousel — CSS-driven, JS shuffle only ----------
     Rotation/positioning is entirely in CSS (animation: rkHeroFocalCycle).
     JS only Fisher-Yates shuffles the DOM order on load so visitors see
     a different 4 products each visit. */
  function initHeroFocal() {
    document.querySelectorAll('[data-rk-hero-focal]').forEach((stage) => {
      const products = Array.from(stage.querySelectorAll('.rk-hero__product'));
      const dots = Array.from(stage.querySelectorAll('.rk-hero__focal-dot'));
      if (products.length < 2) return;

      stage.setAttribute('data-rk-init', 'true');

      // Fisher-Yates shuffle, then re-insert in shuffled DOM order so the
      // first-4 nth-of-type animation delays apply to a fresh subset each load
      for (let k = products.length - 1; k > 0; k--) {
        const j = Math.floor(Math.random() * (k + 1));
        [products[k], products[j]] = [products[j], products[k]];
      }
      const dotsParent = dots[0] ? dots[0].parentNode : null;
      products.forEach((p) => stage.insertBefore(p, dotsParent || null));
    });
  }

  /* ---------- Legacy stub (kept so boot() doesn't error) ---------- */
  function initHeroShuffle() {
    document.querySelectorAll('[data-rk-shuffle]').forEach((stack) => {
      const show = parseInt(stack.dataset.rkShow, 10) || 4;
      const allCards = Array.from(stack.querySelectorAll('.rk-shuffle__card'));
      if (allCards.length === 0) return;

      function shuffleArray(arr) {
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
      }

      const cycle = 12; // must match CSS animation-duration
      const visibleCount = Math.min(show, allCards.length);
      const pool = shuffleArray([...allCards]);
      const initial = pool.splice(0, visibleCount);

      function attachRotation(card, delay) {
        stack.appendChild(card);
        // Force layout flush so the class addition triggers a fresh animation cycle
        // eslint-disable-next-line no-unused-expressions
        void card.offsetWidth;
        card.style.animationDelay = delay;
        card.classList.add('is-in');

        const onIter = () => {
          // Pop a random product from the pool, swap this card out for it
          if (pool.length === 0) return;
          card.removeEventListener('animationiteration', onIter);
          const nextIdx = Math.floor(Math.random() * pool.length);
          const next = pool.splice(nextIdx, 1)[0];
          // Return the just-finished card to the pool
          card.classList.remove('is-in');
          card.style.animationDelay = '';
          pool.push(card);
          // Start the replacement from 0% (no delay) — keeps stagger because
          // each replacement happens at its own iteration end time.
          attachRotation(next, '0s');
        };
        card.addEventListener('animationiteration', onIter);
      }

      initial.forEach((card, idx) => {
        const delay = (-(idx * (cycle / visibleCount))).toFixed(2) + 's';
        attachRotation(card, delay);
      });
    });
  }

  /* ---------- Boot ---------- */
  /* ---------- Collection filters: only one dropdown open at a time ---------- */
  function initFilterAccordion() {
    if (window.__rkFilterAccordion) return;
    window.__rkFilterAccordion = true;
    // `toggle` doesn't bubble → listen in capture phase
    document.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d || !d.matches) return;
      if (d.open && d.matches('.facets__disclosure, .facets details, facet-filters-form details')) {
        document.querySelectorAll('.facets__disclosure[open], .facets details[open], facet-filters-form details[open]').forEach(function (o) {
          if (o !== d) o.open = false;
        });
      }
    }, true);
  }

  /* ---------- Related/Recently carousels: arrows + reveal ---------- */
  function initRelatedRails() {
    document.querySelectorAll('[data-rk-rel-rail]').forEach(function (rail) {
      if (rail.dataset.rkRelInit) return;
      rail.dataset.rkRelInit = '1';
      var head = rail.closest('section, .related-products, [class*="section"]') || document;
      // re-find the nav near this rail
      var scope = rail.parentElement;
      var prev = scope.querySelector('[data-rk-rel-prev]');
      var next = scope.querySelector('[data-rk-rel-next]');
      function step() {
        var card = rail.querySelector('.rk-rel__item, .grid__item');
        return card ? card.getBoundingClientRect().width + 16 : 280;
      }
      function update() {
        if (!prev || !next) return;
        prev.disabled = rail.scrollLeft <= 4;
        next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      }
      if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step() * 2, behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step() * 2, behavior: 'smooth' }); });
      rail.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
      update();
      // reveal on view
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { rail.classList.add('is-in'); io.disconnect(); } });
      }, { threshold: 0.12 });
      io.observe(rail);
    });
  }

  /* Re-init the related rail when Dawn injects recommendations asynchronously */
  function watchRecommendations() {
    document.querySelectorAll('product-recommendations').forEach(function (pr) {
      if (pr.dataset.rkWatch) return;
      pr.dataset.rkWatch = '1';
      var mo = new MutationObserver(function () {
        var rail = pr.querySelector('[data-rk-rel-rail]');
        if (rail) {
          rail.dataset.rkRelInit = ''; initRelatedRails(); rail.classList.add('is-in');
          // these cards are injected by AJAX after boot — wire up the variant-aware
          // links + whole-card (incl. image) click-through that boot() only ran once.
          initCardVariantPicker();
          initCardClickThrough();
        }
      });
      mo.observe(pr, { childList: true, subtree: true });
    });
  }

  /* ---------- Recently viewed (localStorage) ---------- */
  function initRecentlyViewed() {
    var KEY = 'rkRecent';
    var list = [];
    try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { list = []; }

    // 1) record the current product (PDP only)
    var cur = document.querySelector('[data-rk-current-product]');
    var current = null;
    if (cur) {
      try { current = JSON.parse(cur.textContent); } catch (e) {}
      if (current && current.handle) {
        list = list.filter(function (p) { return p.handle !== current.handle; });
        list.unshift(current);
        if (list.length > 12) list = list.slice(0, 12);
        try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
      }
    }

    // 2) render the rail (exclude the current product)
    var rail = document.querySelector('[data-rk-recent-rail]');
    var section = document.querySelector('[data-rk-recent]');
    if (!rail || !section) return;
    var items = list.filter(function (p) { return !current || p.handle !== current.handle; });
    if (items.length < 2) return; // not enough to bother
    rail.innerHTML = items.map(function (p) {
      return '<li class="grid__item rk-rel__item"><a class="rk-recent-card" href="' + p.url + '">' +
        '<span class="rk-recent-card__media"><img src="' + p.image + '" alt="' + (p.title || '').replace(/"/g, '&quot;') + '" loading="lazy" width="300" height="375"></span>' +
        '<span class="rk-recent-card__title">' + (p.title || '') + '</span>' +
        '<span class="rk-recent-card__price">' + (p.price || '') + '</span>' +
        '</a></li>';
    }).join('');
    section.classList.add('is-ready');
    rail.dataset.rkRelInit = ''; // allow initRelatedRails to wire arrows
    initRelatedRails();
  }

  /* ---------- Menu & Search are mutually exclusive ---------- */
  function initMenuSearchExclusive() {
    if (window.__rkMenuSearchExcl) return;
    window.__rkMenuSearchExcl = true;
    function closeMenu() {
      var d = document.querySelector('.menu-drawer-container[open]');
      if (d) d.removeAttribute('open');
    }
    function closeSearch() {
      var sm = document.querySelector('.header__search');
      if (sm && sm.querySelector('details[open]')) {
        // use Dawn's close if available, else just remove [open]
        if (typeof sm.close === 'function') { try { sm.close(false); return; } catch (e) {} }
        var det = sm.querySelector('details[open]'); if (det) det.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }
    }
    // open search → close menu
    var searchSummary = document.querySelector('.header__search summary');
    if (searchSummary) searchSummary.addEventListener('click', closeMenu, true);
    // open menu → close search
    var menuSummary = document.querySelector('summary.header__icon--menu');
    if (menuSummary) menuSummary.addEventListener('click', closeSearch, true);
  }

  function boot() {
    initMenuSearchExclusive();
    initFilterAccordion();
    initRelatedRails();
    watchRecommendations();
    initRecentlyViewed();
    initScrollProgress();
    autoTagParallax();
    autoTagHomepageReveals();
    splitHeadingsForReveal();
    initReveal();
    initParallax();
    initVideoHero();
    initCardVariantPicker();
    initSwatchImageSwap();
    initThumbsScroller();
    initCardClickThrough();
    initCardHoverVideos();
    initPDPDefaultAvailable();
    initPDPColorSwatches();
    initCursor();
    initSmoothAnchors();
    initPinScroll();
    initStickyHeader();
    initMagneticButtons();
    initCollectionRails();
    initHeroLogoScroll();
    initHeroShuffle();
    initHeroFocal();
    initHeroBento();
    initVideoFeatureBento();
    initBentoStack();
    initSectionBgParallax();
    initLookbookDynamic();
    initRkAbout();
    initBuyNowPopup();
    initStatCounters();
  }

  /* ---------- Animated stat counters (count up on scroll into view) ---------- */
  function initStatCounters() {
    var groups = document.querySelectorAll('[data-rk-stats]');
    if (!groups.length) return;
    function fmt(n) { return n.toLocaleString('en-US'); }
    function animate(el) {
      var target = parseInt((el.getAttribute('data-rk-count') || '0').replace(/[^\d]/g, ''), 10) || 0;
      var suffix = el.getAttribute('data-rk-suffix') || '';
      var dur = 3000, start = null;
      function tick(now) {
        if (start === null) start = now;
        var t = Math.min(1, (now - start) / dur);
        var eased = 1 - Math.pow(1 - t, 3);            // ease-out cubic
        el.textContent = fmt(Math.round(target * eased)) + suffix;
        if (t < 1) requestAnimationFrame(tick);
        else el.textContent = fmt(target) + suffix;
      }
      requestAnimationFrame(tick);
    }
    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.rkStatsInit === '1') return;
      group.dataset.rkStatsInit = '1';
      var nums = group.querySelectorAll('[data-rk-count]');
      if (reduceMotion) {
        Array.prototype.forEach.call(nums, function (n) {
          var t = parseInt((n.getAttribute('data-rk-count') || '0').replace(/[^\d]/g, ''), 10) || 0;
          n.textContent = fmt(t) + (n.getAttribute('data-rk-suffix') || '');
        });
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { Array.prototype.forEach.call(nums, animate); io.disconnect(); }
        });
      }, { threshold: 0.4 });
      io.observe(group);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Shopify section reload (theme editor live preview)
  document.addEventListener('shopify:section:load', boot);
})();
